/**
 * useTabSwipe — horizontal swipe → navigate between dashboard tabs (mobile only).
 *
 * Returns a set of touch handlers + drag state to spread on the scope element
 * (typically `<main id="dashboard-main">` in DashboardLayout). The hook is a
 * no-op on desktop/tablet because pointer users don't swipe; we guard at the
 * call site via `useViewport().isMobile`.
 *
 * Gesture lifecycle (v1.4 pass 8 — quality animation refresh):
 *   1. onTouchStart captures (x, y, time, target). If the touch originates
 *      inside a Recharts surface, a horizontally-scrollable table, or a Radix
 *      Dialog, we flag the gesture as `rejected` so we never fight it.
 *   2. onTouchMove tracks finger drift. We keep the gesture "uncommitted" for
 *      the first 10 px of diagonal motion — if vertical dominates, we abort
 *      and let the page scroll. Otherwise we update `dragOffset` so the
 *      caller can translate the main pane in sync with the finger.
 *   3. onTouchEnd: if the gesture meets distance + speed + dominance
 *      thresholds, we trigger `phase = 'committing'` — the caller animates
 *      the pane off-screen in the gesture direction, then we navigate, then
 *      the new content slides in from the opposite edge. If the gesture
 *      failed, we animate back to 0 (`phase = 'springing-back'`).
 *   4. onTransitionEnd drives the state machine from committing → navigating
 *      → entering → idle. We use inline transform + css transition rather
 *      than framer-motion so we don't add a 50 kB dep for one gesture.
 *
 * Preserves `location.search` across navigation (matches the NavLink
 * `to={{ pathname, search }}` pattern so filter state rides along).
 *
 * What it DOES NOT do:
 *   - Does not handle vertical swipes — those are fall-through for page scroll.
 *   - Does not trap touches inside Radix Dialog — Dialogs render in a portal
 *     outside `<main>`, so their touches never reach these handlers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface SwipeTab {
  to: string;
  label?: string;
}

interface TouchState {
  x: number;
  y: number;
  t: number;
  rejected: boolean;
  /**
   * Axis-locked once the user has moved > 10 px in a dominant direction.
   * 'unknown' while we're still figuring out intent so we don't steal page
   * scroll prematurely.
   */
  axis: 'unknown' | 'horizontal' | 'vertical';
}

type Phase =
  | 'idle'
  | 'dragging'
  | 'springing-back'
  | 'committing' // old pane sliding off-screen in gesture direction
  | 'entering'; // new pane sliding in from opposite edge

const MIN_DX = 48;
const MAX_DT_MS = 600;
const DOMINANCE_RATIO = 1.5;
const AXIS_LOCK_PX = 10;
const DRAG_DAMP = 0.85;
const COMMIT_MS = 220;
const SPRING_MS = 240;

/**
 * Walk up from the touched element looking for anything we should let handle
 * the gesture itself (Recharts chart, an actually-scrollable horizontal strip,
 * a Radix Dialog, a drag handle). If any ancestor matches, we tag the gesture
 * as rejected on touchstart and skip the navigation on touchend.
 */
function shouldRejectGesture(target: EventTarget | null, scope: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  let el: Element | null = target;
  while (el && el !== scope) {
    if (el.classList.contains('recharts-surface')) return true;
    if (el.classList.contains('recharts-wrapper')) return true;
    if (el.getAttribute('role') === 'dialog') return true;
    if (el instanceof HTMLElement) {
      const style = window.getComputedStyle(el);
      const overflowX = style.overflowX;
      if (
        (overflowX === 'auto' || overflowX === 'scroll') &&
        el.scrollWidth > el.clientWidth + 1
      ) {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

export interface TabSwipeHandlers {
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: React.TouchEvent<HTMLElement>) => void;
  onTransitionEnd: (e: React.TransitionEvent<HTMLElement>) => void;
  /** Current horizontal offset applied to the pane. 0 when idle. */
  dragOffset: number;
  /** Whether a CSS transition should be active on the pane. */
  animating: boolean;
  /** Transition duration in ms (differs commit vs spring-back). */
  transitionMs: number;
}

export function useTabSwipe(tabs: SwipeTab[], enabled: boolean): TabSwipeHandlers {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const stateRef = useRef<TouchState | null>(null);
  const phaseRef = useRef<Phase>('idle');
  const pendingTabRef = useRef<string | null>(null);
  const directionRef = useRef<1 | -1>(1);

  const [dragOffset, setDragOffset] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [transitionMs, setTransitionMs] = useState(SPRING_MS);

  const resetIdle = useCallback(() => {
    phaseRef.current = 'idle';
    setAnimating(false);
    setDragOffset(0);
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!enabled) return;
      // Mid-animation tap cancels any in-flight spring. The user wants to
      // grab the pane again; snapping transitions off lets us follow the
      // new touch immediately without a jump.
      if (phaseRef.current !== 'idle') {
        setAnimating(false);
      }
      if (e.touches.length !== 1) {
        stateRef.current = null;
        return;
      }
      const t = e.touches[0];
      const scope = e.currentTarget;
      const rejected = shouldRejectGesture(e.target, scope);
      stateRef.current = {
        x: t.clientX,
        y: t.clientY,
        t: performance.now(),
        rejected,
        axis: 'unknown',
      };
    },
    [enabled],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!enabled) return;
      const st = stateRef.current;
      if (!st || st.rejected) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - st.x;
      const dy = t.clientY - st.y;

      if (st.axis === 'unknown') {
        // Defer axis lock until the finger has moved far enough that we can
        // distinguish tap jitter from an actual swipe. Inside the jitter zone
        // we do nothing — page scroll remains responsive.
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          // Vertical intent — abandon the swipe so native scroll takes over.
          stateRef.current = { ...st, axis: 'vertical', rejected: true };
          return;
        }
        stateRef.current = { ...st, axis: 'horizontal' };
      }

      if (stateRef.current?.axis !== 'horizontal') return;

      // No transition during drag — we want the pane to track the finger
      // 1:1 (dampened slightly so rubber-band resistance feels natural).
      phaseRef.current = 'dragging';
      setAnimating(false);
      setDragOffset(dx * DRAG_DAMP);
    },
    [enabled],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!enabled) return;
      const st = stateRef.current;
      stateRef.current = null;
      if (!st || st.rejected || st.axis !== 'horizontal') {
        // Either the touch was rejected up-front or axis-locked to vertical;
        // in either case dragOffset was never moved away from 0.
        return;
      }

      const end = e.changedTouches[0];
      if (!end) {
        resetIdle();
        return;
      }
      const dx = end.clientX - st.x;
      const dy = end.clientY - st.y;
      const dt = performance.now() - st.t;

      const failed =
        dt > MAX_DT_MS ||
        Math.abs(dx) < MIN_DX ||
        Math.abs(dx) < Math.abs(dy) * DOMINANCE_RATIO;

      if (failed) {
        // Spring back to origin.
        phaseRef.current = 'springing-back';
        setTransitionMs(SPRING_MS);
        setAnimating(true);
        setDragOffset(0);
        return;
      }

      // Commit: which tab are we heading to?
      const currentIdx = tabs.findIndex((tab) => pathname.startsWith(tab.to));
      const safeIdx = currentIdx === -1 ? 0 : currentIdx;
      const direction: 1 | -1 = dx < 0 ? 1 : -1;
      const nextIdx = (safeIdx + direction + tabs.length) % tabs.length;
      const nextTab = tabs[nextIdx];
      if (!nextTab) {
        phaseRef.current = 'springing-back';
        setTransitionMs(SPRING_MS);
        setAnimating(true);
        setDragOffset(0);
        return;
      }

      // Slide the old pane off-screen in the gesture direction, then swap
      // and slide in from the opposite edge (see onTransitionEnd).
      phaseRef.current = 'committing';
      pendingTabRef.current = nextTab.to;
      directionRef.current = direction;
      const viewportWidth =
        typeof window !== 'undefined' ? window.innerWidth : 400;
      setTransitionMs(COMMIT_MS);
      setAnimating(true);
      setDragOffset(-direction * viewportWidth);
    },
    [enabled, pathname, resetIdle, tabs],
  );

  const onTransitionEnd = useCallback(
    (_e: React.TransitionEvent<HTMLElement>) => {
      const phase = phaseRef.current;
      if (phase === 'springing-back') {
        phaseRef.current = 'idle';
        setAnimating(false);
        return;
      }
      if (phase === 'committing') {
        const nextTo = pendingTabRef.current;
        pendingTabRef.current = null;
        const direction = directionRef.current;
        const viewportWidth =
          typeof window !== 'undefined' ? window.innerWidth : 400;
        if (!nextTo) {
          resetIdle();
          return;
        }
        // Pre-position the pane on the far side with NO transition so the
        // new content appears off-screen, then the entering-phase rAF below
        // kicks off the slide-in animation.
        phaseRef.current = 'entering';
        setAnimating(false);
        setDragOffset(direction * viewportWidth);
        navigate({ pathname: nextTo, search });
        return;
      }
      if (phase === 'entering') {
        phaseRef.current = 'idle';
        setAnimating(false);
        return;
      }
    },
    [navigate, resetIdle, search],
  );

  // When phase transitions to 'entering' we rAF the pane back to 0 with
  // transition on. Using an effect keyed on dragOffset works because the
  // committing→entering transition sets dragOffset to ±viewportWidth with
  // no animation; that render triggers this effect, which schedules the
  // slide-in on the next frame so the browser has committed the jump
  // before animating.
  useEffect(() => {
    if (phaseRef.current !== 'entering') return;
    // Guard — we only enter the 'entering' phase right after a large jump.
    if (dragOffset === 0) return;
    const raf = requestAnimationFrame(() => {
      setTransitionMs(COMMIT_MS);
      setAnimating(true);
      setDragOffset(0);
    });
    return () => cancelAnimationFrame(raf);
  }, [dragOffset]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTransitionEnd,
    dragOffset,
    animating,
    transitionMs,
  };
}
